/**
 * Lenteurs vues par le visiteur (19/10/2026), jumeau de `browserErrors.ts` : le navigateur
 * mesure ce que ni l'API ni le SSR ne voient, et l'envoie au journal des lenteurs du site
 * (`POST /api/site-latency/client`, écran admin « Lenteurs du site », outils MCP).
 *
 * Quatre mesures, toutes par `PerformanceObserver` (aucun coût quand rien n'est lent) :
 *  - `page_load`   : contenu principal affiché tard (LCP ≥ 2,5 s, seuil « mauvais » de Google) ;
 *  - `interaction` : clic ou frappe dont l'écran répond tard (≥ 300 ms, comme l'INP) ;
 *  - `freeze`      : page figée par une tâche longue (≥ 300 ms) — les « petits lags » ;
 *  - `api_request` : appel /api lent vu du visiteur, réseau compris (≥ 1,5 s).
 * Rien de personnel : ni texte saisi, ni requête d'adresse, ni cookie. Les lots partent quand
 * la page passe en arrière-plan (sendBeacon), ou toutes les 20 s s'il y a quelque chose.
 */

const ENDPOINT = '/api/site-latency/client'
export const PAGE_LOAD_MS = 2500
export const INTERACTION_MS = 300
export const FREEZE_MS = 300
export const API_REQUEST_MS = 1500
/** Au plus 40 mesures par chargement de l'application : une page qui rame ne sature rien. */
const MAX_PER_SESSION = 40
const FLUSH_EVERY_MS = 20_000

export interface LatencyTiming {
  kind: 'page_load' | 'interaction' | 'freeze' | 'api_request'
  durationMs: number
  page: string
  route?: string
  method?: string
  label?: string
  metrics?: Record<string, number>
}

/** Appels longs par nature (WebSocket, flux) ou journaux eux-mêmes : pas des lenteurs. */
export function isMeasuredApiCall(pathname: string): boolean {
  return pathname.startsWith('/api/')
    && !/^\/api\/(?:ws|site-latency|site-errors|track|analytics)\b/.test(pathname)
    && !/\/(?:stream|events)(?:\/|$)/.test(pathname)
}

/**
 * Élément visé, décrit sans rien de saisi : balise, puis libellé accessible ou texte court
 * d'un bouton/lien (« button « Ajouter au panier » »). Jamais la valeur d'un champ.
 */
export function describeTarget(el: Element | null | undefined): string {
  if (!el || typeof el.tagName !== 'string') return ''
  const target = (el.closest?.('button, a, [role="button"], label, input, select, textarea, [data-testid]') ?? el) as Element
  const tag = target.tagName.toLowerCase()
  const isField = /^(input|textarea|select)$/.test(tag)
  const named = target.getAttribute('aria-label') || target.getAttribute('title') || target.getAttribute('data-testid')
    || (isField ? target.getAttribute('name') : (target.textContent || '').replace(/\s+/g, ' ').trim())
  const text = (named || '').slice(0, 60)
  return text ? `${tag} « ${text} »` : tag
}

/** Mesures regroupées par page (un envoi par page), 20 au plus par envoi. */
export function batchesByPage(list: LatencyTiming[]): { page: string; timings: Omit<LatencyTiming, 'page'>[] }[] {
  const groups = new Map<string, Omit<LatencyTiming, 'page'>[]>()
  for (const { page, ...t } of list) {
    const g = groups.get(page) ?? []
    if (g.length < 20) g.push(t)
    groups.set(page, g)
  }
  return [...groups].map(([page, timings]) => ({ page, timings }))
}

let queue: LatencyTiming[] = []
let sent = 0
let installed = false

function push(t: LatencyTiming) {
  if (sent + queue.length >= MAX_PER_SESSION) return
  queue.push({ ...t, durationMs: Math.round(t.durationMs) })
}

function flush() {
  if (!queue.length) return
  const list = queue
  queue = []
  sent += list.length
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType
  for (const batch of batchesByPage(list)) {
    const body = JSON.stringify({ ...batch, context: { language: navigator.language, connection } })
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) continue
      void fetch(ENDPOINT, { method: 'POST', headers: { 'content-type': 'application/json' }, body, credentials: 'include', keepalive: true }).catch(() => {})
    } catch { /* jamais d'erreur depuis la mesure */ }
  }
}

function observe(type: string, onEntries: (entries: PerformanceEntry[]) => void, extra: Record<string, unknown> = {}): boolean {
  if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return false
  try {
    new PerformanceObserver((list) => { try { onEntries(list.getEntries()) } catch { /* mesure seulement */ } }).observe({ type, buffered: true, ...extra } as PerformanceObserverInit)
    return true
  } catch { return false }
}

export function installBrowserLatencyCapture(): void {
  if (installed || typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return
  installed = true
  const page = () => location.pathname
  // Onglet ouvert en arrière-plan : son « chargement » ne mesure pas l'attente d'un visiteur
  let hiddenEarly = document.visibilityState === 'hidden'

  // 1. Chargement : dernier LCP connu, envoyé quand la page passe en arrière-plan.
  let lcp: { ms: number; label: string } | null = null
  const landing = page()
  observe('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1] as PerformanceEntry & { element?: Element | null }
    if (last) lcp = { ms: last.startTime, label: describeTarget(last.element ?? null) }
  })
  const reportLoad = () => {
    if (!lcp || hiddenEarly || lcp.ms < PAGE_LOAD_MS) { lcp = null; return }
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    const fcp = performance.getEntriesByName('first-contentful-paint')[0]?.startTime
    push({
      kind: 'page_load', durationMs: lcp.ms, page: landing, label: lcp.label,
      metrics: {
        lcp: lcp.ms,
        ...(nav ? { ttfb: nav.responseStart, domReady: nav.domContentLoadedEventEnd, transferKb: Math.round((nav.transferSize || 0) / 1024) } : {}),
        ...(fcp ? { fcp } : {}),
      },
    })
    lcp = null
  }

  // 2. Gels : « long-animation-frame » dit QUEL script a bloqué ; sinon « longtask ».
  const onFreeze = (entries: PerformanceEntry[]) => {
    for (const e of entries) {
      if (e.duration < FREEZE_MS) continue
      const scripts = (e as PerformanceEntry & { scripts?: { sourceURL?: string; invoker?: string; duration: number }[] }).scripts ?? []
      const worst = [...scripts].sort((a, b) => b.duration - a.duration)[0]
      const source = worst ? `${worst.invoker || ''} ${String(worst.sourceURL || '').replace(/^.*\/(?=[^/]+$)/, '').replace(/\?.*$/, '')}`.trim() : ''
      push({ kind: 'freeze', durationMs: e.duration, page: page(), label: source || undefined, metrics: worst ? { scriptMs: worst.duration } : undefined })
    }
  }
  if (!observe('long-animation-frame', onFreeze)) observe('longtask', onFreeze)

  // 3. Interactions lentes : du geste à l'image suivante (comme l'INP).
  // Un geste produit plusieurs évènements (pointerdown, pointerup, click) : compté une fois.
  const seen = new Set<number>()
  observe('event', (entries) => {
    if (seen.size > 500) seen.clear()
    for (const e of entries as (PerformanceEventTiming & { interactionId?: number })[]) {
      if (!e.interactionId || e.duration < INTERACTION_MS || seen.has(e.interactionId)) continue
      seen.add(e.interactionId)
      push({
        kind: 'interaction', durationMs: e.duration, page: page(), label: `${e.name} · ${describeTarget(e.target as Element | null)}`,
        metrics: { inputDelay: e.processingStart - e.startTime, processing: e.processingEnd - e.processingStart, presentation: e.startTime + e.duration - e.processingEnd },
      })
    }
  }, { durationThreshold: 200 })

  // 4. Appels d'API lents vus du navigateur (réseau + serveur + téléchargement).
  observe('resource', (entries) => {
    for (const e of entries as PerformanceResourceTiming[]) {
      if (e.duration < API_REQUEST_MS) continue
      let path = ''
      try { const url = new URL(e.name); if (url.origin !== location.origin) continue; path = url.pathname } catch { continue }
      if (!isMeasuredApiCall(path)) continue
      push({
        kind: 'api_request', durationMs: e.duration, page: page(), route: path,
        metrics: { waiting: Math.max(0, e.responseStart - e.requestStart), download: Math.max(0, e.responseEnd - e.responseStart), sizeKb: Math.round((e.transferSize || 0) / 1024) },
      })
    }
  })

  const leave = () => { reportLoad(); flush() }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') leave()
    else if (!lcp) hiddenEarly = true // revenu après coup : le LCP suivant n'est plus un chargement
  })
  window.addEventListener('pagehide', leave)
  window.setInterval(() => { if (queue.length) flush() }, FLUSH_EVERY_MS)
}
